<template>
  <v-data-table
    :loading="loadingMigrationsHistory"
    :items="migrationsHistory"
    :headers="headers"
    :items-per-page="itemsPerPage"
    @pagination="pagination"
    :page.sync="currentPage"
    :footer-props="{
      'items-per-page-options': itemsOptions,
    }"
    :height="migrationHistoryTableHeigth"
    :no-data-text="$t('migrationHistory.noDataTableText')"
    :loading-text="$t('migrationHistory.loadingTableText')"
    fixed-header
    item
    item-key="migration_key"
    show-expand
  >
    <template v-slot:[`header.author`]="{ header }">
      {{ header.text }}
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" dark v-bind="attrs" v-on="on" small
            >ph-question</v-icon
          >
        </template>
        <span class="font-weight-medium">
          {{ $t("migrationHistory.tooltipAuthor") }}
        </span>
      </v-tooltip>
    </template>

    <template v-slot:[`item.created_at`]="{ item }">
      <div class="mr-4">
        {{ formatDateAndHour(item.created_at, true) }}
      </div>
    </template>

    <template v-slot:[`item.event`]="{ item }">
      <div class="mr-4"><Event :code="item.event_code" /></div>
    </template>

    <template v-slot:[`item.status`]="{ item }">
      <div class="mr-4">
        <Status :status="item.status" />
      </div>
    </template>

    <template v-slot:[`item.author`]="{ item }">
      <div class="mr-4">
        <Email :email="item.author.email" :header="'author'" />
      </div>
    </template>

    <template v-slot:[`item.description`]="{ item }">
      <div class="mr-4">
        <Description :history="item" />
      </div>
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <div class="mr-4">
        <Actions :history="item" />
      </div>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td
        v-show="item.event_code === 'EMAIL_TO_GROUP'"
        :colspan="headers.length"
        class="pl-4 pt-4 pb-4 grey lighten-4"
      >
        <div class="mb-6">
          <h3 class="text-body-2">
            <strong>{{ $t("emailMigration.migration_config_info") }}:</strong>
          </h3>
          <ul>
            <li class="py-1">
              {{
                $t("emailMigration.migration_time_range", {
                  start: formatDate(item.start_date),
                  end: formatDate(item.created_at),
                })
              }}
            </li>
            <li v-if="!!item.shared_with?.length" class="py-1">
              {{ $t("migrationHistory.sharedWithHeader") }}:
              {{
                    !!item.shared_with?.length
                      ? item.shared_with.join(", ")
                      : "",
              }}
            </li>
          </ul>
        </div>
        <div>
          <h3 class="text-body-2">
            <strong
              >{{ $t("emailMigration.migration_processing_details") }}:</strong
            >
          </h3>
          <div v-if="loadingDetails.includes(item.migration_key)">
            <v-progress-circular indeterminate size="20" class="mr-2" />
            {{ $t(`emailMigration.loading`) }}
          </div>
          <div v-else-if="migrationDetails[item.migration_key]">
            <ul>
              <div
                v-for="(value, key) in migrationDetails[item.migration_key]"
                :key="key"
              >
                <li v-if="key != 'context' && key != 'status'" class="py-1">
                  <span> {{ $t(`emailMigration.${key}`) }}: </span>
                  <span
                    v-html="
                      getMigrationValues(
                        key,
                        migrationDetails[item.migration_key]
                      )
                    "
                  ></span>
                </li>
              </div>
            </ul>
          </div>
          <div v-else class="mt-2">
            <div v-if="item.status == 'IN_QUEUE'">
              {{ $t(`emailMigration.no_details_in_queue`) }}
            </div>
            <div v-else-if="item.status == 'CANCELED'">
              {{ $t(`emailMigration.no_details_canceled`) }}
            </div>
            <div v-else-if="item.status == 'INTERRUPTED'">
              {{ $t(`emailMigration.no_details_interrupted`) }}
            </div>
            <div v-else>
              <Alert dense color="warning" class="w-auto">
                {{ $t("emailMigration.no_details_error") }}
              </Alert>
            </div>
          </div>
        </div>
      </td>
    </template>

    <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
      <v-icon
        v-show="item.event_code === 'EMAIL_TO_GROUP'"
        :class="[
          'v-data-table__expand-icon',
          { 'v-data-table__expand-icon--active': isExpanded },
        ]"
        @click.stop="handleExpand(item, isExpanded, expand)"
        >$expand</v-icon
      >
    </template>
  </v-data-table>
</template>
<script>
import Actions from "@/components/migration/cells/Actions.vue";
import Description from "@/components/migration/cells/Description.vue";
import Email from "@/components/migration/cells/Email.vue";
import Event from "@/components/migration/cells/Event.vue";
import Status from "@/components/migration/cells/Status.vue";
import {
  convertBytesInMegas,
  formatDate,
  formatDateAndHour,
} from "@/helpers/services/utils";
import { getLabelsHistory } from "@/helpers/variables/migrations";
import { paging } from "@/helpers/variables/tablesDefault";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "MigrationHistoryTable",

  components: {
    Actions,
    Description,
    Email,
    Event,
    Status,
  },
  data() {
    return {
      migrationHistoryTableHeigth: "calc(95vh - 220px)",
      currentPage: 1,
      itemsPerPage: paging.perPage,
      itemsOptions: paging.All,
      loadingDetails: [],
    };
  },
  computed: {
    ...mapGetters([
      "migrationsHistory",
      "migrationDetails",
      "loadingMigrationsHistory",
    ]),

    labelsHistory() {
      return getLabelsHistory();
    },

    headers() {
      return [
        {
          value: "created_at",
          text: this.labelsHistory.created_at,
          align: "start",
          sortable: true,
          width: 180,
        },
        {
          value: "event",
          text: this.labelsHistory.event,
          align: "start",
          sortable: false,
        },
        {
          value: "status",
          text: this.labelsHistory.status,
          align: "start",
          sortable: false,
        },
        {
          value: "author",
          text: this.labelsHistory.author,
          sortable: false,
          align: "start",
        },
        {
          value: "description",
          text: this.labelsHistory.description,
          align: "start",
          sortable: false,
          width: 700,
        },
        {
          value: "actions",
          text: this.labelsHistory.actions,
          align: "start",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    formatDateAndHour,
    formatDate,

    convertBytesInMegas,

    ...mapActions(["getMigrationsHistory", "getMigrationDetails"]),

    pagination(event) {
      this.currentPage = event.page;
    },

    getMigrationValues(key, item) {
      switch (key) {
        case "total_messages_size":
          return `${convertBytesInMegas(item[key])} mb`;
      }
      return item[key] !== undefined && item[key] !== null
        ? item[key]
        : this.$t("emailMigration.not_found");
    },

    async handleExpand(item, isExpanded, expand) {
      expand(!isExpanded);

      if (!isExpanded) {
        if (item.event_code != "EMAIL_TO_GROUP") {
          return;
        }

        this.loadingDetails.push(item.migration_key);

        try {
          await this.getMigrationDetails(item.migration_key);
        } finally {
          this.loadingDetails = this.loadingDetails.filter(
            (id) => id !== item.migration_key
          );
        }
      }
    },
  },
  beforeMount() {
    this.getMigrationsHistory();
  },
};
</script>
